<template>
  <div class="invoice-approval" v-if="agreement !== null">
    <el-dialog
      title="Согласовать счёт на оплату"
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      width="96%"
      custom-class="invoice-approval__dialog"
      @closed="closeDialog"
    >
      <div class="invoice-approval__heading">
        <div>
          <span class="invoice-approval__eyebrow">Согласование</span>
          <h2>Счёт на оплату</h2>
        </div>
        <div class="invoice-approval__date" v-if="agreement.date">
          <span>Дата задачи</span>
          <strong>{{ agreement.date }}</strong>
        </div>
      </div>

      <section v-for="section in sections" :key="section.title" class="invoice-approval__section">
        <h3>{{ section.title }}</h3>
        <dl class="invoice-approval__grid">
          <div
            v-for="field in section.fields"
            :key="field.key"
            class="invoice-approval__field"
            :class="{ 'invoice-approval__field--wide': field.wide }"
          >
            <dt>{{ field.label }}</dt>
            <dd>{{ displayValue(field) }}</dd>
          </div>
        </dl>
      </section>

      <section class="invoice-approval__section">
        <h3>Параметры оплаты</h3>
        <dl class="invoice-approval__grid invoice-approval__payment-procedure">
          <div class="invoice-approval__field invoice-approval__field--wide">
            <dt>{{ paymentProcedureField.label }}</dt>
            <dd>{{ displayValue(paymentProcedureField) }}</dd>
          </div>
        </dl>
        <div
          v-for="(payment, paymentIndex) in paymentInvoices"
          :key="`payment-${paymentIndex}`"
          class="invoice-approval__payment"
        >
          <h4 v-if="paymentInvoices.length > 1">Платёж {{ paymentIndex + 1 }}</h4>
          <dl class="invoice-approval__grid">
            <div v-for="field in paymentFields" :key="field.key" class="invoice-approval__field">
              <dt>{{ field.label }}</dt>
              <dd>{{ displayValue(field, payment) }}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section v-if="attachedFiles.length" class="invoice-approval__section invoice-approval__files">
        <div class="invoice-approval__files-heading">
          <h3>Вложения</h3>
          <span>{{ attachedFiles.length }}</span>
        </div>
        <ul>
          <li v-for="(file, index) in attachedFiles" :key="fileKey(file, index)">
            <button type="button" :disabled="downloadingFile === index" @click="downloadFile(file, index)">
              <span class="invoice-approval__file-icon">
                <i :class="downloadingFile === index ? 'el-icon-loading' : 'el-icon-document'" />
              </span>
              <span class="invoice-approval__file-name">{{ fileName(file, index) }}</span>
              <i class="el-icon-download invoice-approval__download-icon" />
            </button>
          </li>
        </ul>
      </section>

      <section class="invoice-approval__section invoice-approval__comment">
        <h3>Комментарий</h3>
        <el-input
          v-model="comment"
          type="textarea"
          :rows="4"
          maxlength="1000"
          show-word-limit
          placeholder="Введите комментарий"
        />
      </section>

      <div slot="footer" class="invoice-approval__actions">
        <el-button size="small" type="danger" plain :disabled="submitting" @click="openRejectDialog">
          <i class="el-icon-close" /> Отклонить
        </el-button>
        <el-button size="small" type="success" :loading="submitting" @click="confirmAccept">
          <i class="el-icon-check" /> Согласовать
        </el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="Комментарий к отклонению"
      :visible.sync="rejectDialogVisible"
      :close-on-click-modal="false"
      width="92%"
      custom-class="invoice-approval__reject-dialog"
      append-to-body
      @closed="resetRejectDialog"
    >
      <p class="invoice-approval__reject-hint">
        Укажите причину отклонения. Комментарий будет передан в 1С:CRM.
      </p>
      <el-input
        ref="rejectComment"
        v-model="comment"
        type="textarea"
        :rows="4"
        maxlength="1000"
        show-word-limit
        placeholder="Введите комментарий"
        :class="{ 'invoice-approval__comment--error': commentError }"
        @input="commentError = false"
      />
      <p v-if="commentError" class="invoice-approval__error">
        Комментарий обязателен для отклонения задачи
      </p>
      <div slot="footer" class="invoice-approval__reject-actions">
        <el-button size="small" :disabled="submitting" @click="rejectDialogVisible = false">Отмена</el-button>
        <el-button size="small" type="danger" :loading="submitting" @click="rejectAgreement">Отклонить</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from "axios";
import downloadjs from "downloadjs";
import { ADRESS } from "@/store/actions/auth";

const INVOICE_FIELDS = [
  { key: "Basis", aliases: ["Основание"], label: "Основание", wide: true },
  { key: "IncomingNumber", aliases: ["ВходящийНомер"], label: "Входящий номер" },
  { key: "IncomingDate", aliases: ["ВходящаяДата"], label: "Входящая дата", type: "date" },
  { key: "namecounterparty", aliases: ["Контрагент"], label: "Контрагент", wide: true },
  { key: "inn", aliases: ["Контрагент ИНН", "КонтрагентИНН", "ИНН"], label: "ИНН контрагента" },
  { key: "kpp", aliases: ["Контрагент КПП", "КонтрагентКПП", "КПП"], label: "КПП контрагента" },
  { key: "ContractNumber", aliases: ["НомерДоговора"], label: "Номер договора" },
  { key: "ContractDate", aliases: ["ДатаДоговора"], label: "Дата договора", type: "date" },
  { key: "Organization", aliases: ["Организация"], label: "Организация", wide: true },
  { key: "author", aliases: ["Автор"], label: "Автор", wide: true },
  { key: "Amount", aliases: ["Сумма"], label: "Сумма" },
  { key: "Сurrency", aliases: ["Валюта"], label: "Валюта счёта" },
  { key: "VAT", aliases: ["НДС"], label: "НДС" },
  { key: "Purpose", aliases: ["НазначениеПлатежа", "Назначение платежа"], label: "Назначение платежа", wide: true },
];

const PAYMENT_PROCEDURE_FIELD = {
  key: "PaymentProcedure",
  aliases: ["Процедура оплаты", "ПроцедураОплаты"],
  label: "Процедура оплаты",
};

const PAYMENT_FIELDS = [
  { key: "ShareProcent", aliases: ["Доля"], label: "Доля" },
  { key: "PaymentAmount", aliases: ["СуммаОплаты"], label: "Сумма оплаты" },
  { key: "Currency", aliases: ["ВалютаОплаты"], label: "Валюта оплаты" },
  { key: "PaymentAmountRUB", aliases: ["СуммаОплатыРуб"], label: "Сумма оплаты, руб." },
  { key: "ExchangeRate", aliases: ["Курс"], label: "Курс" },
  { key: "RateDate", aliases: ["ДатаКурса"], label: "Дата курса", type: "date" },
];

export default {
  name: "vInvoiceAgreementView",
  props: {
    propAgreement: Object,
    activities: String,
  },
  data() {
    return {
      agreement: null,
      agreementData: {},
      dialogVisible: false,
      rejectDialogVisible: false,
      comment: "",
      commentError: false,
      submitting: false,
      downloadingFile: null,
      sections: [
        { title: "Реквизиты счёта", fields: INVOICE_FIELDS },
      ],
      paymentProcedureField: PAYMENT_PROCEDURE_FIELD,
      paymentFields: PAYMENT_FIELDS,
    };
  },
  computed: {
    attachedFiles() {
      const files = this.agreementData.files || this.agreementData["Файлы"] || [];
      return Array.isArray(files) ? files : [];
    },
    paymentInvoices() {
      const payments = this.agreementData.PaymentInvoicelist;
      if (Array.isArray(payments) && payments.length) return payments;

      // Сохраняем совместимость с ответом, где поля оплаты были на верхнем уровне.
      return [this.agreementData];
    },
  },
  watch: {
    propAgreement(agreement) {
      if (!this.isInvoiceApproval(agreement)) return;

      this.agreement = agreement;
      this.agreementData = { ...agreement };
      this.dialogVisible = true;
      this.getAgreementData();
    },
  },
  methods: {
    isInvoiceApproval(agreement) {
      if (!agreement) return false;

      const taskName = String(agreement["Задача"] || "").trim().toLowerCase().replace(/ё/g, "е");
      const businessProcess = String(agreement["БизнесПроцесс"] || "").trim().toLowerCase();

      return (
        taskName.includes("согласовать счет на оплату") ||
        (!taskName && (
          businessProcess === "согласование счетов" ||
          businessProcess === "согласование счетов на оплату"
        ))
      );
    },
    getFieldValue(field, source = this.agreementData) {
      const keys = [field.key].concat(field.aliases || []);

      for (let index = 0; index < keys.length; index += 1) {
        const value = source[keys[index]];
        if (value !== null && value !== undefined && String(value).trim() !== "") return value;
      }

      return null;
    },
    displayValue(field, source) {
      const value = this.getFieldValue(field, source || this.agreementData);
      if (value === null) return "—";
      if (field.type === "date") return this.formatDate(value);
      if (typeof value === "boolean") return value ? "Да" : "Нет";
      if (Array.isArray(value)) return value.join(", ");
      if (typeof value === "object") return value.name || value.value || JSON.stringify(value);
      return value;
    },
    formatDate(value) {
      const date = String(value).trim();
      const isoDate = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoDate) return `${isoDate[3]}.${isoDate[2]}.${isoDate[1]}`;

      const russianDate = date.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
      if (russianDate) return `${russianDate[1]}.${russianDate[2]}.${russianDate[3]}`;

      return date;
    },
    fileLink(file) {
      if (!file) return "";

      const directLink = file.href || file.link || file.url;
      if (directLink) return String(directLink);

      if (typeof file.name === "string" && file.name.includes("<")) {
        const container = document.createElement("div");
        container.innerHTML = file.name;
        const link = container.querySelector("a");
        if (link) return link.getAttribute("href") || "";
      }

      return file.id || file.uik || file["УИК"] || "";
    },
    fileToken(file) {
      return String(this.fileLink(file))
        .trim()
        .replace(/^file:\/\//i, "")
        .replace(/^file:/i, "")
        .replace(/^file/i, "");
    },
    fileName(file, index) {
      if (!file) return `Файл ${index + 1}`;

      const rawName = file.name || file["Название"] || file.filename;
      if (!rawName) return `Файл ${index + 1}`;
      if (typeof rawName !== "string" || !rawName.includes("<")) return String(rawName);

      const container = document.createElement("div");
      container.innerHTML = rawName;
      return container.textContent.trim() || `Файл ${index + 1}`;
    },
    fileKey(file, index) {
      return this.fileToken(file) || `${this.fileName(file, index)}-${index}`;
    },
    downloadFile(file, index) {
      const token = this.fileToken(file);
      if (!token) {
        this.$notify.error({
          title: "Ошибка",
          message: "Не удалось определить идентификатор файла",
        });
        return;
      }

      this.downloadingFile = index;
      axios
        .post(`${ADRESS}clickhandling`, token, {
          headers: { Authorization: this.activities },
        })
        .then((response) => {
          const fileData = response.data && response.data.value ? response.data.value : response.data;
          if (!fileData || !fileData.body) throw new Error("Сервер не вернул содержимое файла");
          downloadjs(fileData.body, fileData.name || this.fileName(file, index));
        })
        .catch((err) => {
          this.$notify.error({ title: "Ошибка скачивания", message: this.errorMessage(err) });
        })
        .finally(() => {
          this.downloadingFile = null;
        });
    },
    getAgreementData() {
      const loading = this.$loading({ lock: true });

      axios
        .get(`${ADRESS}gettask`, {
          headers: { Authorization: this.activities },
          params: {
            taskid: this.agreement["УИК"],
            type: "t",
          },
        })
        .then((response) => {
          const responseData = response.data || {};
          const taskData = responseData.value && typeof responseData.value === "object"
            ? responseData.value
            : {};

          // gettask возвращает служебные данные в value, а реквизиты
          // согласования счёта может передавать на верхнем уровне ответа.
          this.agreementData = {
            ...this.agreement,
            ...taskData,
            ...responseData,
          };
        })
        .catch((err) => {
          this.$notify.error({ title: "Ошибка", message: this.errorMessage(err) });
        })
        .finally(() => loading.close());
    },
    confirmAccept() {
      this.$confirm("Согласовать счёт на оплату?", "Подтверждение", {
        confirmButtonText: "Согласовать",
        cancelButtonText: "Отмена",
        confirmButtonClass: "el-button--success",
        type: "success",
      })
        .then(() => this.submitDecision(true, this.comment.trim()))
        .catch(() => {});
    },
    openRejectDialog() {
      this.rejectDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.rejectComment) this.$refs.rejectComment.focus();
      });
    },
    rejectAgreement() {
      const comment = this.comment.trim();
      if (!comment) {
        this.commentError = true;
        return;
      }
      this.submitDecision(false, comment);
    },
    submitDecision(isAccepted, comment) {
      this.submitting = true;

      return axios({
        method: "post",
        url: `${ADRESS}accepttask`,
        params: {
          // Для согласования счетов в 1С:CRM передаётся УИК самой задачи,
          // а не УИК родительского объекта.
          taskid: this.agreement["УИК"],
          accept: isAccepted,
          idtaskaccept: this.agreementData.idtaskaccept,
          comment,
        },
        headers: { Authorization: this.activities },
      })
        .then(() => {
          this.$notify.success({
            title: "Готово",
            message: isAccepted ? "Счёт согласован" : "Счёт отклонён",
          });
          const completedUik = this.agreement["УИК"];
          this.rejectDialogVisible = false;
          this.dialogVisible = false;
          this.$emit("complete", completedUik);
        })
        .catch((err) => {
          this.$notify.error({ title: "Ошибка", message: this.errorMessage(err) });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    errorMessage(err) {
      return (err.response && err.response.data && (err.response.data.message || err.response.data)) || err.message || "Не удалось выполнить запрос";
    },
    resetRejectDialog() {
      if (this.submitting) return;
      this.comment = "";
      this.commentError = false;
    },
    closeDialog() {
      this.agreement = null;
      this.agreementData = {};
      this.comment = "";
      this.commentError = false;
      this.rejectDialogVisible = false;
      this.downloadingFile = null;
      this.$emit("closeDialog");
    },
  },
};
</script>

<style lang="scss">
.invoice-approval__dialog {
  max-width: 780px;
  border-radius: 14px;
  overflow: hidden;

  .el-dialog__header { padding: 18px 22px 12px; border-bottom: 1px solid #e8edf2; }
  .el-dialog__title { font-size: 15px; font-weight: 600; }
  .el-dialog__body { padding: 0 22px 22px; color: #1d2935; }
  .el-dialog__footer { padding: 14px 22px; border-top: 1px solid #e8edf2; background: #f8fafb; }
}

.invoice-approval__heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  padding: 24px 0 18px;

  h2 { margin: 4px 0 0; font-size: 23px; line-height: 1.2; color: #17232f; }
}

.invoice-approval__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: #168159;
}

.invoice-approval__date {
  flex: 0 0 auto;
  text-align: right;
  span, strong { display: block; }
  span { margin-bottom: 3px; font-size: 11px; color: #7a8793; }
  strong { font-size: 13px; }
}

.invoice-approval__section {
  margin-top: 12px;
  padding: 18px;
  border: 1px solid #e5ebef;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 6px 22px rgba(31, 48, 61, 0.045);
  h3 { margin: 0 0 14px; font-size: 13px; font-weight: 700; color: #344553; }
}

.invoice-approval__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0;
  margin: 0;
}

.invoice-approval__field {
  min-width: 0;
  padding: 11px 14px 11px 0;
  border-top: 1px solid #edf1f4;

  &:nth-child(-n + 2) { border-top: 0; padding-top: 0; }
  &--wide { grid-column: 1 / -1; }
  dt { margin-bottom: 4px; font-size: 11px; line-height: 1.35; color: #7b8792; }
  dd { margin: 0; font-size: 13px; font-weight: 600; line-height: 1.45; color: #1e2c38; white-space: pre-wrap; overflow-wrap: anywhere; }
}

.invoice-approval__payment-procedure {
  margin-bottom: 4px;
}

.invoice-approval__payment {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #dfe7eb;

  h4 {
    margin: 2px 0 12px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #168159;
  }
}

.invoice-approval__files {
  background: #f8fbfa;

  ul {
    display: grid;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li button {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #dce7e2;
    border-radius: 8px;
    background: #fff;
    color: #253740;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

    &:hover:not(:disabled) {
      border-color: #75ae96;
      box-shadow: 0 5px 14px rgba(31, 82, 61, 0.08);
      transform: translateY(-1px);
    }

    &:disabled {
      cursor: wait;
      opacity: 0.65;
    }
  }
}

.invoice-approval__files-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  h3 { margin: 0; }

  span {
    min-width: 20px;
    padding: 2px 6px;
    border-radius: 10px;
    background: #dfeee7;
    color: #39745a;
    font-size: 10px;
    font-weight: 700;
    text-align: center;
  }
}

.invoice-approval__file-icon {
  display: grid;
  flex: 0 0 32px;
  height: 32px;
  margin-right: 10px;
  place-items: center;
  border-radius: 7px;
  background: #e8f3ed;
  color: #168159;
  font-size: 16px;
}

.invoice-approval__file-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-approval__download-icon {
  margin-left: 10px;
  color: #668076;
  font-size: 15px;
}

.invoice-approval__actions,
.invoice-approval__reject-actions { display: flex; justify-content: flex-end; gap: 8px; }

.invoice-approval__reject-dialog {
  max-width: 520px;
  border-radius: 12px;
  .el-dialog__body { padding-top: 8px; }
}

.invoice-approval__reject-hint { margin: 0 0 14px; font-size: 13px; line-height: 1.45; color: #5e6c78; }
.invoice-approval__comment--error textarea { border-color: #f56c6c; }
.invoice-approval__error { margin: 6px 0 0; font-size: 12px; color: #f56c6c; }

@media (max-width: 550px) {
  .invoice-approval__dialog {
    margin-top: 2vh !important;
    margin-bottom: 2vh;
    .el-dialog__header { padding: 15px 38px 11px 14px; }
    .el-dialog__body { padding: 0 12px 14px; }
    .el-dialog__footer { position: sticky; bottom: 0; z-index: 2; padding: 11px 12px; }
  }

  .invoice-approval__heading {
    align-items: flex-start;
    padding: 18px 2px 12px;
    h2 { font-size: 19px; }
  }

  .invoice-approval__date { max-width: 42%; }
  .invoice-approval__section { padding: 14px; }
  .invoice-approval__grid { grid-template-columns: minmax(0, 1fr); }

  .invoice-approval__field,
  .invoice-approval__field:nth-child(-n + 2) {
    grid-column: 1;
    padding: 10px 0;
    border-top: 1px solid #edf1f4;
    &:first-child { padding-top: 0; border-top: 0; }
  }

  .invoice-approval__actions {
    justify-content: stretch;
    .el-button { flex: 1; margin-left: 0; }
  }
}
</style>
